import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import YouTube from "react-youtube";
import {
  FaPlay,
  FaPause,
  FaSearch,
  FaStar,
  FaWaveSquare,
  FaExternalLinkAlt,
  FaChevronDown,
  FaChevronUp,
  FaMusic,
} from "react-icons/fa";
import SEO from "./SEO";
import playlistsManifest from "../assets/livestreaming/playlists.json";
import playlistItemsMap from "../assets/livestreaming/playlistItems.json";
import "./DJPage.css";

const CHANNEL_URL = "https://www.youtube.com/@gcc-worship";

const FALLBACK_TRACKS = [
  {
    id: "score-01",
    title: "Sitting In The Sun (Walking In The Rain)",
    artist: "DumitrEscu",
    bpm: 128,
    duration: "2:00",
    youtubeId: "nCkpzqqog4k",
    playlistId: "fallback",
  },
  {
    id: "score-02",
    title: "Let Me Clear My Throat (Extended)",
    artist: "DJ Kool",
    bpm: 132,
    duration: "1:59",
    youtubeId: "OWqes1NLk4s",
    playlistId: "fallback",
  },
  {
    id: "score-03",
    title: "Hold Me (Original Mix)",
    artist: "Jessica Lane",
    bpm: 125,
    duration: "4:55",
    youtubeId: "tM2vr7YV-C8",
    playlistId: "fallback",
  },
];

const formatReleaseDate = (value) => {
  if (!value) return "TBD";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "TBD";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const parseTitleAndArtist = (value) => {
  if (!value || typeof value !== "string") {
    return { title: value || "Untitled", artist: null };
  }
  const separators = [" - ", " – ", " — ", "|"];
  for (const separator of separators) {
    const parts = value
      .split(separator)
      .map((segment) => segment.trim())
      .filter(Boolean);
    if (parts.length >= 2) {
      return {
        artist: parts[0],
        title: parts.slice(1).join(separator).trim(),
      };
    }
  }
  return { title: value.trim(), artist: null };
};

const playlistMetaMap = Array.isArray(playlistsManifest)
  ? playlistsManifest.reduce((acc, playlist) => {
      if (playlist?.id) {
        acc[playlist.id] = playlist;
      }
      return acc;
    }, {})
  : {};

const buildTrackLibrary = () => {
  if (!playlistItemsMap || typeof playlistItemsMap !== "object") return FALLBACK_TRACKS;
  const tracks = [];
  const playlistOrder = Array.isArray(playlistsManifest)
    ? playlistsManifest.map((playlist) => playlist.id)
    : Object.keys(playlistItemsMap);
  playlistOrder.forEach((playlistId) => {
    const playlistMeta = playlistMetaMap[playlistId];
    const entries = playlistItemsMap[playlistId] || [];
      entries.forEach((item, index) => {
        const isPrivate =
          typeof item.description === "string" &&
          item.description.toLowerCase().includes("private");
        if (isPrivate) return;
        const { artist: parsedArtist, title: parsedTitle } = parseTitleAndArtist(
          item.title,
        );
        tracks.push({
          id: `${playlistId}-${item.id}`,
          title: parsedTitle || item.title || "Untitled",
          artist: parsedArtist || playlistMeta?.title || "#gcc-worship",
          bpm: 90 + (index * 5) % 60,
          duration: item.duration || "3:00",
          release: formatReleaseDate(item.publishedAt),
          youtubeId: item.id,
          playlistId,
          genre: playlistMeta?.title || "#gcc-worship",
        });
      });
    });
  return tracks.length ? tracks : FALLBACK_TRACKS;
};

const TRACK_LIBRARY = buildTrackLibrary();
const INTELLIGENT_FILTERS = [
  { id: "all", label: "All", description: "All tracks", predicate: () => true },
  { id: "high-energy", label: "High", description: "High Energy (≥120 BPM)", predicate: (track) => (track.bpm || 0) >= 120 },
  { id: "chill", label: "Chill", description: "Chill (<100 BPM)", predicate: (track) => (track.bpm || 0) < 100 },
  { id: "mid-tempo", label: "Groove", description: "Groove (100-115 BPM)", predicate: (track) => {
      const bpm = track.bpm || 0;
      return bpm >= 100 && bpm <= 115;
    } },
];
const SORT_KEYS = ["title", "bpm", "artist", "duration"];
const SAMPLE_BANKS = [
  [
    { id: "pad-snare", label: "Snare", url: "https://actions.google.com/sounds/v1/cartoon/cartoon_boing.ogg" },
    { id: "pad-clap", label: "Clap", url: "https://actions.google.com/sounds/v1/alarms/alarm_clock.ogg" },
    { id: "pad-ride", label: "Ride", url: "https://actions.google.com/sounds/v1/foley/metal_scrape.ogg" },
    { id: "pad-kick", label: "Kick", url: "https://actions.google.com/sounds/v1/alarms/beep_short.ogg" },
    { id: "pad-hat", label: "Hat", url: "https://actions.google.com/sounds/v1/alarms/beep_short.ogg" },
    { id: "pad-synth", label: "Synth", url: "https://actions.google.com/sounds/v1/cartoon/cartoon_cowbell.ogg" },
    { id: "pad-bass", label: "Bass", url: "https://actions.google.com/sounds/v1/cartoon/cartoon_cowbell.ogg" },
    { id: "pad-vocal", label: "Vocal", url: "https://actions.google.com/sounds/v1/cartoon/cartoon_boing.ogg" },
  ],
  [
    { id: "pad-perc", label: "Perc", url: "https://actions.google.com/sounds/v1/cartoon/cartoon_cowbell.ogg" },
    { id: "pad-choir", label: "Choir", url: "https://actions.google.com/sounds/v1/cartoon/cartoon_boing.ogg" },
    { id: "pad-bell", label: "Bell", url: "https://actions.google.com/sounds/v1/cartoon/cartoon_cowbell.ogg" },
    { id: "pad-snap", label: "Snap", url: "https://actions.google.com/sounds/v1/cartoon/cartoon_boing.ogg" },
    { id: "pad-vox", label: "Vox", url: "https://actions.google.com/sounds/v1/cartoon/cartoon_boing.ogg" },
    { id: "pad-pad", label: "Pad", url: "https://actions.google.com/sounds/v1/cartoon/cartoon_cowbell.ogg" },
    { id: "pad-sweep", label: "Sweep", url: "https://actions.google.com/sounds/v1/cartoon/cartoon_cowbell.ogg" },
    { id: "pad-perc2", label: "Perc 2", url: "https://actions.google.com/sounds/v1/cartoon/cartoon_boing.ogg" },
  ],
];
const PLAYLIST_TRACK_MAP = TRACK_LIBRARY.reduce((acc, track) => {
  if (!track?.playlistId) return acc;
  if (!acc[track.playlistId]) {
    acc[track.playlistId] = [];
  }
  acc[track.playlistId].push(track);
  return acc;
}, {});

const FX_OPTIONS = ["Echo", "Reverb", "Flanger", "Phaser", "Filter", "Roll", "Beat"];
const ANNOTATION_SECTIONS = [
  { id: "verse", label: "Verse", beats: 32, bars: 8, description: "Steady pulsing groove" },
  { id: "chorus", label: "Chorus", beats: 48, bars: 12, description: "Wavy synth uplift" },
  { id: "bridge", label: "Bridge", beats: 16, bars: 4, description: "Percussive breakdown" },
  { id: "outro", label: "Outro", beats: 24, bars: 6, description: "Ambient fade-out" },
];

const YOUTUBE_OPTS = {
  width: "100%",
  height: "200",
  playerVars: {
    autoplay: 0,
    controls: 1,
    rel: 0,
    modestbranding: 1,
  },
};

const DJPage = () => {
  useEffect(() => {
    const handleScriptError = (event) => {
      if (event?.message === "Script error." && !event?.error?.stack) {
        event.preventDefault?.();
        event.stopImmediatePropagation?.();
        return true;
      }
      return false;
    };
    window.addEventListener("error", handleScriptError, true);
    return () => {
      window.removeEventListener("error", handleScriptError, true);
    };
  }, []);
  const youtubeOpts = useMemo(() => {
    const origin = typeof window !== "undefined" ? window.location.origin : "http://localhost:3000";
    return {
      ...YOUTUBE_OPTS,
      playerVars: {
        ...YOUTUBE_OPTS.playerVars,
        origin,
      },
    };
  }, []);
  const playlistFilters = useMemo(() => {
    const base = [{ id: "all", title: "All" }];
    if (!Array.isArray(playlistsManifest)) return base;
    const filters = playlistsManifest
      .filter((playlist) => playlist?.id && playlist?.title)
      .map((playlist) => ({ id: playlist.id, title: playlist.title }));
    if (!filters.length) return base;
    return [...base, ...filters];
  }, []);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPlaylist, setSelectedPlaylist] = useState(() => playlistFilters[0]?.id || "all");
  const [crossfade, setCrossfade] = useState(50);
  const [beatJump, setBeatJump] = useState(4);
  const [boostActive, setBoostActive] = useState(false);
  const [leftDeck, setLeftDeck] = useState(TRACK_LIBRARY[0]);
  const [rightDeck, setRightDeck] = useState(TRACK_LIBRARY[1] || TRACK_LIBRARY[0]);
  const [hasMounted, setHasMounted] = useState(false);
  const [leftPlaying, setLeftPlaying] = useState(false);
  const [rightPlaying, setRightPlaying] = useState(false);
  const leftPlayer = useRef(null);
  const rightPlayer = useRef(null);
  const [playlistWidth, setPlaylistWidth] = useState(30);

  const [syncEnabled, setSyncEnabled] = useState(true);
  const [keySyncEnabled, setKeySyncEnabled] = useState(true);
  const [quantizeEnabled, setQuantizeEnabled] = useState(true);
  const [loopActive, setLoopActive] = useState(false);
  const loopSizes = [1, 2, 4, 8, 16, 32];
  const [loopSizeIndex, setLoopSizeIndex] = useState(2);
  const loopSizeValue = loopSizes[loopSizeIndex];
  const [fxBank, setFxBank] = useState(1);
  const [fxDepth, setFxDepth] = useState(50);
  const beatJumpOptions = [1, 2, 4, 8, 16];
  const [headphoneCueDeck, setHeadphoneCueDeck] = useState("left");
  const [headphoneCueMix, setHeadphoneCueMix] = useState(50);
  const [dvsEnabled, setDvsEnabled] = useState(false);
  const [recordingActive, setRecordingActive] = useState(false);
  const [sampleBankIndex, setSampleBankIndex] = useState(0);
  const [sampleVolume, setSampleVolume] = useState(80);
  const [sampleTrim, setSampleTrim] = useState(60);
  const [sampleLoopMode, setSampleLoopMode] = useState("one-shot");
  const [sampleSync, setSampleSync] = useState(true);
  const [sortBy, setSortBy] = useState("title");
  const [sortDirection, setSortDirection] = useState("asc");
  const [intelligentFilter, setIntelligentFilter] = useState("all");
  const [previewTrack, setPreviewTrack] = useState(null);
  const [activeAnnotation, setActiveAnnotation] = useState(ANNOTATION_SECTIONS[0]?.id || "verse");
  const defaultDeckFeatures = {
    loopActive: false,
    loopIndex: 2,
    keyShift: 0,
    slip: false,
    reverse: false,
    hotCue: 1,
    pitchBend: 0,
    trim: 50,
    tempo: 100,
    waveformPhase: Math.random() * Math.PI * 2,
    eq: {
      high: 60,
      mid: 50,
      low: 50,
    },
    filter: "none",
    filterLevel: 50,
    stems: {
      vocals: true,
      drums: true,
      bass: true,
      melody: true,
    },
  };
  const [deckFeatures, setDeckFeatures] = useState({
    left: { ...defaultDeckFeatures },
    right: { ...defaultDeckFeatures },
  });
  const [collapsedPanels, setCollapsedPanels] = useState({
    deckOne: false,
    deckTwo: false,
    master: false,
    playlists: false,
    tracks: false,
  });

  const togglePanel = useCallback((panel) => {
    setCollapsedPanels((prev) => ({
      ...prev,
      [panel]: !prev[panel],
    }));
  }, []);

  const renderPanelToggle = useCallback(
    (panelKey, label) => {
      const collapsed = collapsedPanels[panelKey];
      const Icon = collapsed ? FaChevronDown : FaChevronUp;
      return (
        <button
          type="button"
          className="panel-toggle"
          onClick={() => togglePanel(panelKey)}
          aria-expanded={!collapsed}
          aria-label={collapsed ? `Expand ${label}` : `Collapse ${label}`}
        >
          <Icon aria-hidden="true" />
          <span className="sr-only">{collapsed ? `Expand ${label}` : `Collapse ${label}`}</span>
        </button>
      );
    },
    [collapsedPanels, togglePanel]
  );

  const activeAnnotationData =
    ANNOTATION_SECTIONS.find((section) => section.id === activeAnnotation) || ANNOTATION_SECTIONS[0];

  const filteredTracks = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    const baseTracks =
      selectedPlaylist === "all"
        ? TRACK_LIBRARY
        : PLAYLIST_TRACK_MAP[selectedPlaylist] || [];
    const activeFilter = INTELLIGENT_FILTERS.find((filter) => filter.id === intelligentFilter);
    const filtered = baseTracks.filter((track) => {
      const matchesSearch =
        !query ||
        track.title.toLowerCase().includes(query) ||
        track.artist?.toLowerCase().includes(query);
      const matchesIntelligent = activeFilter ? activeFilter.predicate(track) : true;
      return matchesSearch && matchesIntelligent;
    });
    const prioritized = [...filtered];
    prioritized.sort((a, b) => {
      const getValue = (item) => {
        if (sortBy === "bpm") return item.bpm || 0;
        if (sortBy === "duration") return Number(item.duration?.split(":")?.[0] || 0) * 60;
        return (item[sortBy] ?? "").toString().toLowerCase();
      };
      const valueA = getValue(a);
      const valueB = getValue(b);
      if (valueA === valueB) return 0;
      if (sortDirection === "asc") {
        return valueA > valueB ? 1 : -1;
      }
      return valueA < valueB ? 1 : -1;
    });
    return prioritized;
  }, [searchTerm, selectedPlaylist, intelligentFilter, sortBy, sortDirection]);

  const cueOffsets = [0, 10, 30, 60];

  const updatePlayerVolume = useCallback(() => {
    const leftVolumeBase = Math.max(0, Math.round((100 - crossfade) * 0.95));
    const rightVolumeBase = Math.max(0, Math.round(crossfade * 0.95));
    const leftTrimMultiplier = (deckFeatures.left?.trim ?? 50) / 100;
    const rightTrimMultiplier = (deckFeatures.right?.trim ?? 50) / 100;
    const leftVolume = Math.round(leftVolumeBase * leftTrimMultiplier);
    const rightVolume = Math.round(rightVolumeBase * rightTrimMultiplier);
    leftPlayer.current?.setVolume?.(leftVolume);
    rightPlayer.current?.setVolume?.(rightVolume);
  }, [crossfade, deckFeatures]);

  useEffect(() => {
    updatePlayerVolume();
  }, [updatePlayerVolume]);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  const handleLeftReady = useCallback(
    (event) => {
      leftPlayer.current = event.target;
      updatePlayerVolume();
    },
    [updatePlayerVolume]
  );

  const handleRightReady = useCallback(
    (event) => {
      rightPlayer.current = event.target;
      updatePlayerVolume();
    },
    [updatePlayerVolume]
  );

  useEffect(() => {
    if (!hasMounted) return;
    if (leftPlayer.current?.loadVideoById && leftDeck?.youtubeId) {
      leftPlayer.current.loadVideoById({ videoId: leftDeck.youtubeId });
    }
  }, [leftDeck, hasMounted]);

  useEffect(() => {
    if (!hasMounted) return;
    if (rightPlayer.current?.loadVideoById && rightDeck?.youtubeId) {
      rightPlayer.current.loadVideoById({ videoId: rightDeck.youtubeId });
    }
  }, [rightDeck, hasMounted]);

  const handleTrackLoad = useCallback((track, destination) => {
    if (destination === "left") {
      setLeftDeck(track);
    } else {
      setRightDeck(track);
    }
  }, []);

  const handleSortChange = (key) => {
    setSortBy((prev) => {
      if (prev === key) {
        setSortDirection((dir) => (dir === "asc" ? "desc" : "asc"));
        return prev;
      }
      setSortDirection("asc");
      return key;
    });
  };

  const handleIntelligentFilter = (filterId) => {
    setIntelligentFilter(filterId);
  };

  const handlePreviewTrack = (track) => {
    setPreviewTrack(track);
    setHeadphoneCueDeck("left");
  };

  const handlePlayBoth = useCallback(() => {
    leftPlayer.current?.playVideo?.();
    rightPlayer.current?.playVideo?.();
  }, []);

  const handlePauseBoth = useCallback(() => {
    leftPlayer.current?.pauseVideo?.();
    rightPlayer.current?.pauseVideo?.();
  }, []);

  const handlePauseDeck = useCallback((side) => {
    const player = side === "left" ? leftPlayer.current : rightPlayer.current;
    player?.pauseVideo?.();
  }, []);

  const handleStateChange = useCallback((side, event) => {
    const playing = event?.data === YouTube.PlayerState?.PLAYING;
    if (side === "left") {
      setLeftPlaying(playing);
    } else {
      setRightPlaying(playing);
    }
  }, []);

  const toggleLoopMode = () => {
    setLoopActive((prev) => !prev);
  };

  const adjustLoopSize = (direction) => {
    setLoopSizeIndex((prev) => {
      if (direction === "increase") {
        return Math.min(prev + 1, loopSizes.length - 1);
      }
      return Math.max(prev - 1, 0);
    });
  };

  const toggleSync = () => {
    setSyncEnabled((prev) => !prev);
  };

  const toggleQuantize = () => {
    setQuantizeEnabled((prev) => !prev);
  };


  const cycleFxBank = () => {
    setFxBank((prev) => (prev % 3) + 1);
  };

  const changeBeatJump = (direction) => {
    setBeatJump((prev) => {
      const index = beatJumpOptions.indexOf(prev);
      const next =
        direction === "increase" ? Math.min(index + 1, beatJumpOptions.length - 1) : Math.max(0, index - 1);
      return beatJumpOptions[next];
    });
  };

  const handleFxDial = (value) => {
    setFxDepth(Number(value));
  };

  const handleSampleTrigger = useCallback(
    (sample) => {
      const audio = new Audio(sample.url);
      const baseVolume = Math.min(1, Math.max(0, sampleVolume / 100));
      const trimFactor = Math.min(1, Math.max(0, sampleTrim / 100));
      audio.volume = baseVolume * trimFactor;
      audio.loop = sampleLoopMode !== "one-shot";
      audio.play().catch(() => {});
    },
    [sampleLoopMode, sampleVolume]
  );

  const handleSampleBankChange = (direction) => {
    setSampleBankIndex((prev) => {
      const next = direction === "next" ? prev + 1 : prev - 1;
      if (next < 0) return SAMPLE_BANKS.length - 1;
      if (next >= SAMPLE_BANKS.length) return 0;
      return next;
    });
  };

  const handleSampleLoopMode = (mode) => {
    setSampleLoopMode(mode);
  };

  const handleHeadphoneSelection = (deck) => {
    setHeadphoneCueDeck(deck);
  };

  const monitorLabels = {
    left: "Deck One",
    right: "Deck Two",
    both: "Both decks",
  };

  const handleFxOption = (effect) => {
    setActiveFx(effect);
  };

  const handleFxTiming = (timing) => {
    setFxTiming(timing);
  };

  const handleFxBoost = useCallback(() => {
    setBoostActive((prev) => !prev);
  }, []);

  const updateDeckFeature = (side, callback) => {
    setDeckFeatures((prev) => ({
      ...prev,
      [side]: callback(prev[side]),
    }));
  };

  const toggleDeckLoop = (side) =>
    updateDeckFeature(side, (current) => ({
      ...current,
      loopActive: !current.loopActive,
    }));

  const changeDeckLoopSize = (side, direction) =>
    updateDeckFeature(side, (current) => {
      const next = direction === "increase" ? Math.min(current.loopIndex + 1, loopSizes.length - 1) : Math.max(current.loopIndex - 1, 0);
      return { ...current, loopIndex: next };
    });

  const adjustDeckKey = (side, delta) =>
    updateDeckFeature(side, (current) => {
      const shifted = Math.min(6, Math.max(-6, current.keyShift + delta));
      return { ...current, keyShift: shifted };
    });

  const toggleDeckSlip = (side) =>
    updateDeckFeature(side, (current) => ({ ...current, slip: !current.slip }));

  const toggleDeckReverse = (side) =>
    updateDeckFeature(side, (current) => ({ ...current, reverse: !current.reverse }));

  const triggerDeckPitchBend = (side, amount) => {
    updateDeckFeature(side, (current) => ({ ...current, pitchBend: amount }));
    window.setTimeout(() => {
      updateDeckFeature(side, (current) => ({ ...current, pitchBend: 0 }));
    }, 360);
  };

  const selectDeckHotCue = (side, value) =>
    updateDeckFeature(side, (current) => ({ ...current, hotCue: value }));

  const adjustDeckTempo = (side, value) =>
    updateDeckFeature(side, (current) => ({
      ...current,
      tempo: Number(value),
      waveformPhase: current.waveformPhase + 0.1,
    }));

  const adjustDeckTempoBy = (side, delta) => {
    const currentTempo = deckFeatures[side]?.tempo ?? 100;
    const nextTempo = Math.min(140, Math.max(80, currentTempo + delta));
    adjustDeckTempo(side, nextTempo);
  };

  const renderDeckDial = (side, label) => {
    const settings = deckFeatures[side];
    const boundedTempo = Math.min(140, Math.max(80, settings?.tempo ?? 100));
    const normalized = (boundedTempo - 80) / 60;
    const pointerAngle = normalized * 270 - 135;

    return (
      <div className="deck-dial" aria-label={`${label} tempo dial`}>
        <div className="dial-face">
          <div className="dial-ring" />
          <div className="dial-pointer" style={{ transform: `rotate(${pointerAngle}deg)` }} />
          <span className="dial-value">{boundedTempo.toFixed(1)} BPM</span>
        </div>
        <div className="dial-actions">
          <button
            type="button"
            onClick={() => adjustDeckTempoBy(side, -0.5)}
            aria-label={`Decrease ${label} tempo`}
          >
            −
          </button>
          <span className="dial-stat">Tempo</span>
          <button
            type="button"
            onClick={() => adjustDeckTempoBy(side, 0.5)}
            aria-label={`Increase ${label} tempo`}
          >
            +
          </button>
        </div>
      </div>
    );
  };

  const adjustDeckEq = (side, band, value) =>
    updateDeckFeature(side, (current) => ({
      ...current,
      eq: {
        ...current.eq,
        [band]: Number(value),
      },
    }));

  const toggleDeckFilter = (side, filterType) =>
    updateDeckFeature(side, (current) => ({
      ...current,
      filter: current.filter === filterType ? "none" : filterType,
    }));

  const adjustDeckFilterLevel = (side, value) =>
    updateDeckFeature(side, (current) => ({
      ...current,
      filterLevel: Number(value),
    }));

  const toggleStem = (side, stem) =>
    updateDeckFeature(side, (current) => ({
      ...current,
      stems: {
        ...current.stems,
        [stem]: !current.stems[stem],
      },
    }));

  const BEAT_FX_LENGTHS = ["1/4", "1/2", "1", "2", "4", "8"];
  const [activeFx, setActiveFx] = useState("Echo");
  const [fxTiming, setFxTiming] = useState("1/4");

  const handleDeckPlay = (side) => {
    const player = side === "left" ? leftPlayer.current : rightPlayer.current;
    player?.playVideo?.();
  };

  const handleDeckPause = (side) => {
    const player = side === "left" ? leftPlayer.current : rightPlayer.current;
    player?.pauseVideo?.();
  };

  const toggleKeySync = () => {
    setKeySyncEnabled((prev) => !prev);
  };

  const adjustDeckTrim = (side, value) =>
    updateDeckFeature(side, (current) => ({
      ...current,
      trim: Number(value),
    }));

  const toggleDVS = () => {
    setDvsEnabled((prev) => !prev);
  };

  const toggleRecording = () => {
    setRecordingActive((prev) => !prev);
  };

  const beatGridStatus = useMemo(() => {
    const leftBpm = leftDeck?.bpm || 0;
    const rightBpm = rightDeck?.bpm || 0;
    const delta = Math.abs(leftBpm - rightBpm);
    if (delta < 1) return "Locked";
    if (delta < 3) return "Close";
    return "Needs match";
  }, [leftDeck, rightDeck]);

  const renderDeckWaveform = (settings) => {
    const steps = Array.from({ length: 8 }, (_, index) => {
      const variation = Math.sin(index + settings.waveformPhase) * 20;
      return 40 + variation;
    });
    return (
      <div className="deck-waveform" title="Visual representation of beats and breakdowns">
        {steps.map((height, index) => (
          <span
            key={index}
            className="deck-waveform__bar"
            style={{ height: `${height}px`, width: `${100 / steps.length}%` }}
          />
        ))}
      </div>
    );
  };

  const getVUMeterLevels = (playing) =>
    Array.from({ length: 4 }, (_, index) => (playing ? 60 + index * 10 : 20 + index * 5));

  const renderDeckVUMeter = (side) => {
    const playing = side === "left" ? leftPlaying : rightPlaying;
    const heights = getVUMeterLevels(playing);
    return (
      <div className="vu-meter" title={`VU meter for ${side === "left" ? "Deck One" : "Deck Two"}`}>
        {heights.map((height, index) => (
          <span key={`${side}-vu-${index}`} style={{ height: `${height}px` }} />
        ))}
      </div>
    );
  };

  const renderDeckExtras = (side, label) => {
    const settings = deckFeatures[side];
    const loopValue = loopSizes[settings.loopIndex] || loopSizes[0];
    return (
      <div className="deck-panel" aria-label={`${label} extras`}>
        {renderDeckDial(side, label)}
        <div className="deck-panel__header">
          <span>{label} Features</span>
          <small>Hot cue {settings.hotCue}</small>
        </div>
        <div className="deck-panel__row">
          <button
            type="button"
            className={`deck-panel__pill${settings.loopActive ? " active" : ""}`}
            onClick={() => toggleDeckLoop(side)}
            title={`Toggle looping for ${label}`}
          >
            Loop {settings.loopActive ? "On" : "Off"}
          </button>
          <div className="deck-panel__loop">
            <button
              type="button"
              onClick={() => changeDeckLoopSize(side, "decrease")}
              title={`Shrink the loop on ${label}`}
            >
              —
            </button>
            <span>{loopValue}×</span>
            <button
              type="button"
              onClick={() => changeDeckLoopSize(side, "increase")}
              title={`Expand the loop on ${label}`}
            >
              +
            </button>
          </div>
          <button
            type="button"
            className={`deck-panel__pill${settings.slip ? " active" : ""}`}
            onClick={() => toggleDeckSlip(side)}
            title={`Toggle slip mode for ${label}`}
          >
            Slip
          </button>
          <button
            type="button"
            className={`deck-panel__pill${settings.reverse ? " active" : ""}`}
            onClick={() => toggleDeckReverse(side)}
            title={`Toggle reverse playback for ${label}`}
          >
            Reverse
          </button>
        </div>
        <div className="deck-panel__row channel-row deck-slider-row">
          <span className="deck-panel__label">Channel Fader</span>
          <div className="slider-track">
            <input
              type="range"
              min="0"
              max="100"
              value={settings.trim}
              onChange={(event) => adjustDeckTrim(side, event.target.value)}
              title={`Set ${label} trim/gain`}
            />
          </div>
          <span className="tone-value">{settings.trim}%</span>
        </div>
        <div className="deck-panel__row tempo-row">
          <span className="deck-panel__label">Tempo</span>
          <div className="slider-track">
            <input
              type="range"
              min="80"
              max="140"
              value={settings.tempo}
              onChange={(event) => adjustDeckTempo(side, event.target.value)}
              title={`Adjust ${label} tempo`}
            />
          </div>
          <span className="tone-value">{settings.tempo.toFixed(1)} BPM</span>
        </div>
        <div className="deck-panel__row">
          <span className="deck-panel__label">Waveform</span>
          {renderDeckWaveform(settings)}
        </div>
        <div className="deck-panel__row eq-row">
          {["high", "mid", "low"].map((band) => (
            <div key={`${side}-${band}`} className="eq-slider">
              <span className="eq-label">{band.toUpperCase()}</span>
              <div className="slider-track">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={settings.eq[band]}
                  onChange={(event) => adjustDeckEq(side, band, event.target.value)}
                  title={`${band.toUpperCase()} EQ for ${label}`}
                />
              </div>
              <span className="tone-value">{settings.eq[band]}</span>
            </div>
          ))}
        </div>
        <div className="deck-panel__row filter-row">
          <div className="filter-pill-group">
            {["hpf", "lpf"].map((filterType) => (
              <button
                key={`${side}-${filterType}`}
                type="button"
                className={`deck-panel__pill${settings.filter === filterType ? " active" : ""}`}
                onClick={() => toggleDeckFilter(side, filterType)}
                title={`Toggle ${filterType === "hpf" ? "High-pass" : "Low-pass"} filter`}
              >
                {filterType.toUpperCase()}
              </button>
            ))}
          </div>
          <div className="slider-track">
            <input
              type="range"
              min="0"
              max="100"
              value={settings.filterLevel}
              onChange={(event) => adjustDeckFilterLevel(side, event.target.value)}
              title={`Adjust ${label} filter intensity`}
            />
            <span className="tone-value">{settings.filterLevel}%</span>
          </div>
        </div>
        <div className="deck-panel__row stems-row">
          {Object.entries(settings.stems).map(([stem, enabled]) => (
            <button
              key={`${side}-stem-${stem}`}
              type="button"
              className={`deck-panel__pill${enabled ? " active" : ""}`}
              onClick={() => toggleStem(side, stem)}
              title={`Toggle ${stem} stem`}
            >
              {stem.charAt(0).toUpperCase() + stem.slice(1)}
            </button>
          ))}
        </div>
        <div className="deck-panel__row deck-panel__hotcues">
          {[1, 2, 3, 4].map((index) => (
            <button
              key={`${side}-hotcue-${index}`}
              type="button"
              className={`deck-panel__hotcue${settings.hotCue === index ? " active" : ""}`}
              onClick={() => selectDeckHotCue(side, index)}
              title={`${label} hot cue ${index}`}
            >
              Cue {index}
            </button>
          ))}
        </div>
        <div className="deck-panel__row tone-row">
          <button type="button" onClick={() => adjustDeckKey(side, -1)} title={`Transpose ${label} down`}>
            Key -
          </button>
          <span className="tone-value">
            {settings.keyShift >= 0 ? `+${settings.keyShift}` : settings.keyShift} semitones
          </span>
          <button type="button" onClick={() => adjustDeckKey(side, 1)} title={`Transpose ${label} up`}>
            Key +
          </button>
        </div>
        <div className="deck-panel__row tone-row">
          <button type="button" onClick={() => triggerDeckPitchBend(side, -6)} title={`Bend ${label} pitch down`}>
            Pitch ▼
          </button>
          <span className="tone-value">
            {settings.pitchBend === 0 ? "Centered" : `${settings.pitchBend}%`}
          </span>
          <button type="button" onClick={() => triggerDeckPitchBend(side, 6)} title={`Bend ${label} pitch up`}>
            Pitch ▲
          </button>
        </div>
        {renderDeckVUMeter(side)}
      </div>
    );
  };

  const handleCuePoint = useCallback((slot) => {
    const target = slot < 6 ? leftPlayer.current : rightPlayer.current;
    const offset = cueOffsets[Math.min(cueOffsets.length - 1, slot - 1)];
    target?.seekTo?.(offset, true);
  }, []);

  const handleBeatJump = useCallback(() => {
    const seconds = (beatJump * 60) / (leftDeck?.bpm || 120);
    [leftPlayer.current, rightPlayer.current].forEach((player) => {
      if (!player?.getCurrentTime) return;
      const current = player.getCurrentTime();
      player.seekTo(current + seconds, true);
    });
  }, [beatJump, leftDeck]);

  return (
    <div className="dj-page">
      <SEO
        title="DJ"
        description="Mix audio with dual decks, BPM control, loops, and FX."
      />
      <section className="stage-section">
        <div className="stage-wrapper">
          <div className={`deck deck--left panel${collapsedPanels.deckOne ? " panel--collapsed" : ""}`}>
            <div className="deck__header panel-header">
              <div className="panel-title">
                <p>Deck One</p>
                <span>{leftDeck.bpm} BPM</span>
              </div>
              {renderPanelToggle("deckOne", "Deck One")}
            </div>
            {!collapsedPanels.deckOne && (
              <>
                <div className="deck-mini-actions">
                  <button type="button" onClick={() => handleDeckPlay("left")} title="Play Deck One">
                    ▶
                  </button>
                  <button type="button" onClick={() => handleDeckPause("left")} title="Pause Deck One">
                    ❚❚
                  </button>
                </div>
                <div className="deck-cues-inline">
                  {[1, 2, 3, 4].map((value) => (
                    <button
                      key={`left-inline-${value}`}
                      type="button"
                      onClick={() => handleCuePoint(value)}
                      title={`Jump to cue ${value} on Deck One`}
                    >
                      {value}
                    </button>
                  ))}
                </div>
                {hasMounted ? (
                  <YouTube
                    videoId={leftDeck.youtubeId}
                    opts={youtubeOpts}
                    onReady={handleLeftReady}
                    onStateChange={(event) => handleStateChange("left", event)}
                    className="deck__player"
                  />
                ) : (
                  <div className="deck__player deck__player--placeholder">Loading Deck One…</div>
                )}
                {renderDeckExtras("left", "Deck One")}
              </>
            )}
          </div>
          <div className={`center-column panel${collapsedPanels.master ? " panel--collapsed" : ""}`}>
            <div className="panel-header center-panel__header">
              <span>Master Controls</span>
              {renderPanelToggle("master", "Master Controls")}
            </div>
            {!collapsedPanels.master && (
              <div className="panel-body center-panel__body">
                <div className="waveform-panel small">
                  <p className="time-label">Live BPM</p>
                  <strong>{Math.round((leftDeck.bpm + rightDeck.bpm) / 2)}</strong>
                  <div className="phase-meter">
                    <span className="phase-bar" style={{ width: `${crossfade}%` }} />
                  </div>
                  <div className="waveform-controls-grid">
                    <div className="gain-slider">
                      <div className="gain-label">Deck One {100 - crossfade}%</div>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={crossfade}
                        onChange={(event) => setCrossfade(Number(event.target.value))}
                        title="Balance the volume between Deck One and Deck Two"
                      />
                      <div className="gain-label">Deck Two {crossfade}%</div>
                    </div>
                    <div className="playback-actions">
                      <button
                        className="play-both"
                        type="button"
                        onClick={handlePlayBoth}
                        title="Play both decks simultaneously"
                      >
                        <FaPlay />
                        <span>Play</span>
                      </button>
                      <button type="button" onClick={handlePauseBoth} title="Pause playback on both decks">
                        <FaPause />
                        <span>Pause</span>
                      </button>
                      <button type="button" onClick={() => handlePauseDeck("left")} title="Pause #1">
                        <FaPause />
                        <span>Pause #1</span>
                      </button>
                      <button type="button" onClick={() => handlePauseDeck("right")} title="Pause #2">
                        <FaPause />
                        <span>Pause #2</span>
                      </button>
                      <button
                        type="button"
                        className={`boost-btn${boostActive ? " active" : ""}`}
                        onClick={handleFxBoost}
                        title="Toggle the FX boost on the master strip"
                      >
                        <FaWaveSquare />
                        <span>{boostActive ? "Boosting" : "FX Boost"}</span>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="control-matrix">
                  <div className="matrix-column">
                    <p>Transport</p>
                    <div className="matrix-row">
                      <button
                        type="button"
                        className={`control-pill${syncEnabled ? " active" : ""}`}
                        onClick={toggleSync}
                        title="Keep both decks in sync"
                      >
                        Sync {syncEnabled ? "On" : "Off"}
                      </button>
                      <button
                        type="button"
                        className={`control-pill${quantizeEnabled ? " active" : ""}`}
                        onClick={toggleQuantize}
                        title="Quantize cues and loops to the beat grid"
                      >
                        Quantize
                      </button>
                    </div>
                    <div className="matrix-row">
                      <button
                        type="button"
                        className={`control-pill${loopActive ? " active" : ""}`}
                        onClick={toggleLoopMode}
                        title="Enable automatic looping for both decks"
                      >
                        Auto Loop
                      </button>
                    </div>
                    <div className="matrix-row">
                      <button
                        type="button"
                        className={`control-pill${keySyncEnabled ? " active" : ""}`}
                        onClick={toggleKeySync}
                        title="Lock the musical key across decks"
                      >
                        Key Sync {keySyncEnabled ? "On" : "Off"}
                      </button>
                    </div>
                    <div className="matrix-row beatjump-trigger">
                      <button
                        type="button"
                        className="control-pill"
                        onClick={handleBeatJump}
                        title="Jump forward by the selected beat length"
                      >
                        Beat Jump {beatJump} beats
                      </button>
                    </div>
                    <div className="matrix-row beatjump-row">
                      <button
                        type="button"
                        className="loop-btn"
                        onClick={() => changeBeatJump("decrease")}
                        title="Shorten the beat jump"
                      >
                        -
                      </button>
                      <span className="tone-value">{beatJump} beats step</span>
                      <button
                        type="button"
                        className="loop-btn"
                        onClick={() => changeBeatJump("increase")}
                        title="Lengthen the beat jump"
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <div className="matrix-column">
                    <p>Loop / Pitch</p>
                    <div className="matrix-row">
                      <button
                        type="button"
                        className="loop-btn"
                        onClick={() => adjustLoopSize("decrease")}
                        title="Decrease the global loop size"
                      >
                        -
                      </button>
                      <div className="loop-status" title="Global loop size">
                        <strong>{loopSizeValue}×</strong>
                        <span>{loopActive ? "Running" : "Ready"}</span>
                      </div>
                      <button
                        type="button"
                        className="loop-btn"
                        onClick={() => adjustLoopSize("increase")}
                        title="Increase the global loop size"
                      >
                        +
                      </button>
                    </div>
                    <div className="matrix-row note-row">
                      <span className="tone-value">Deck loop controls still apply per deck</span>
                    </div>
                  </div>
                  <div className="matrix-column">
                    <p>FX Dial</p>
                    <div className="matrix-row fx-dial-row">
                      <div className="fx-dial-wrapper" title="Rotate to set FX depth">
                        <div className="fx-dial">
                          <span
                            className="fx-dial-pointer"
                            style={{ transform: `rotate(${fxDepth * 1.8 - 90}deg)` }}
                          />
                        </div>
                        <span className="fx-dial-value">{fxDepth}%</span>
                      </div>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={fxDepth}
                      onChange={(event) => handleFxDial(event.target.value)}
                      className="fx-dial-input"
                      title="Drag to adjust the FX depth for the boost"
                    />
                    <div className="matrix-row fx-row">
                      <button
                        type="button"
                        className="control-pill"
                        onClick={cycleFxBank}
                        title="Cycle FX banks"
                      >
                        Bank {fxBank}
                      </button>
                      <span className="tone-value">Depth {boostActive ? "High" : "Mid"}</span>
                      <button
                        type="button"
                        className="control-pill"
                        onClick={handleFxBoost}
                        title="Toggle the FX boost for the master strip"
                      >
                        {boostActive ? "Sustain" : "Trigger"}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="effects-panel">
                  <p>FX Effects</p>
                  <div className="effects-row">
                    {FX_OPTIONS.map((effect) => (
                      <button
                        key={effect}
                        type="button"
                        className={`effect-pill${activeFx === effect ? " active" : ""}`}
                        onClick={() => handleFxOption(effect)}
                        title={`Apply ${effect}`}
                      >
                        {effect}
                      </button>
                    ))}
                  </div>
                  <div className="effects-row timing-row">
                    {BEAT_FX_LENGTHS.map((timing) => (
                      <button
                        key={`timing-${timing}`}
                        type="button"
                        className={`effect-pill timing${fxTiming === timing ? " active" : ""}`}
                        onClick={() => handleFxTiming(timing)}
                        title={`Apply ${timing} beat timing`}
                      >
                        {timing}
                      </button>
                    ))}
                  </div>
                  <div className="effects-row effect-status">
                    <span>{activeFx} @ {fxTiming} beats · Depth {fxDepth}%</span>
                  </div>
                </div>
                <div className="monitor-panel">
                  <p>Monitoring</p>
                  <div className="monitor-row">
                    {["left", "right", "both"].map((deck) => (
                      <button
                        key={deck}
                        type="button"
                        className={`control-pill${headphoneCueDeck === deck ? " active" : ""}`}
                        onClick={() => handleHeadphoneSelection(deck)}
                        title={`Send ${monitorLabels[deck]} to the headphones`}
                      >
                        {monitorLabels[deck]}
                      </button>
                    ))}
                  </div>
                  <div className="monitor-row monitor-slider">
                    <label htmlFor="headphone-mix">Headphone mix</label>
                    <input
                      id="headphone-mix"
                      type="range"
                      min="0"
                      max="100"
                      value={headphoneCueMix}
                      onChange={(event) => setHeadphoneCueMix(Number(event.target.value))}
                      title="Blend between deck monitor and master output"
                    />
                    <span>{headphoneCueMix}%</span>
                  </div>
                </div>
                <div className="sampler-panel sampler-panel--compact" aria-label="Sampler pads">
                  <p>Sampler Pad</p>
                  <div className="sampler-row sampler-row--pads">
                    {SAMPLE_BANKS[sampleBankIndex].map((sample) => (
                      <button
                        key={sample.id}
                        type="button"
                        className="sampler-pad"
                        onClick={() => handleSampleTrigger(sample)}
                        title={`Play ${sample.label} sample`}
                      >
                        {sample.label}
                      </button>
                    ))}
                  </div>
                  <div className="sampler-controls">
                    <div className="sampler-bank">
                      <button
                        type="button"
                        className="control-pill"
                        onClick={() => handleSampleBankChange("prev")}
                        title="Previous sample bank"
                      >
                        ‹ Bank
                      </button>
                      <span>{`Bank ${sampleBankIndex + 1}/${SAMPLE_BANKS.length}`}</span>
                      <button
                        type="button"
                        className="control-pill"
                        onClick={() => handleSampleBankChange("next")}
                        title="Next sample bank"
                      >
                        Bank ›
                      </button>
                    </div>
                    <div className="sampler-inline-group">
                      <div className="sampler-slider-row">
                        <div className="sampler-slider-block">
                          <label htmlFor="sample-volume">Volume</label>
                          <input
                            id="sample-volume"
                            type="range"
                            min="0"
                            max="100"
                            value={sampleVolume}
                            onChange={(event) => setSampleVolume(Number(event.target.value))}
                            title="Adjust sampler volume"
                          />
                        </div>
                        <div className="sampler-slider-block">
                          <label htmlFor="sample-trim">Trim</label>
                          <input
                            id="sample-trim"
                            type="range"
                            min="0"
                            max="100"
                            value={sampleTrim}
                            onChange={(event) => setSampleTrim(Number(event.target.value))}
                            title="Adjust sampler trim/gain"
                          />
                        </div>
                      </div>
                      <div className="sampler-row sampler-row--modes">
                        {["one-shot", "loop", "hold"].map((mode) => (
                          <button
                            key={mode}
                            type="button"
                            className={`effect-pill${sampleLoopMode === mode ? " active" : ""}`}
                            onClick={() => handleSampleLoopMode(mode)}
                            title={`Trigger mode: ${mode}`}
                          >
                            {mode.replace("-", " ")}
                          </button>
                        ))}
                      </div>
                      <div className="sampler-row sampler-row--sync">
                        <label htmlFor="sample-sync">Sync to BPM</label>
                        <input
                          id="sample-sync"
                          type="checkbox"
                          checked={sampleSync}
                          onChange={() => setSampleSync((prev) => !prev)}
                          title="Snap sample playback to the master BPM"
                        />
                        <span>{sampleSync ? "Synced" : "Free"}</span>
                      </div>
                    </div>
                </div>
                <small>Instantly trigger percussion for live builds.</small>
              </div>
              <div className="annotation-panel">
                <div className="annotation-heading">
                  <FaMusic aria-hidden="true" />
                  <span>Annotations</span>
                </div>
                <div className="annotation-buttons">
                  {ANNOTATION_SECTIONS.map((section) => (
                    <button
                      key={section.id}
                      type="button"
                      className={`annotation-pill${activeAnnotation === section.id ? " active" : ""}`}
                      onClick={() => setActiveAnnotation(section.id)}
                      title={section.description}
                    >
                      {section.label}
                    </button>
                  ))}
                </div>
                <div className="annotation-summary">
                  <strong>{activeAnnotationData.label}</strong>
                  <span>{activeAnnotationData.description}</span>
                  <div className="annotation-metrics">
                    <span>{activeAnnotationData.beats} beats</span>
                    <span>{activeAnnotationData.bars} bars</span>
                  </div>
                  <div className="annotation-bar">
                    <span
                      style={{
                        width: `${Math.min(100, (activeAnnotationData.bars / 12) * 100)}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          </div>
          <div className={`deck deck--right panel${collapsedPanels.deckTwo ? " panel--collapsed" : ""}`}>
            <div className="deck__header panel-header">
              <div className="panel-title">
                <p>Deck Two</p>
                <span>{rightDeck.bpm} BPM</span>
              </div>
              {renderPanelToggle("deckTwo", "Deck Two")}
            </div>
            {!collapsedPanels.deckTwo && (
              <>
                <div className="deck-mini-actions">
                  <button type="button" onClick={() => handleDeckPlay("right")} title="Play Deck Two">
                    ▶
                  </button>
                  <button type="button" onClick={() => handleDeckPause("right")} title="Pause Deck Two">
                    ❚❚
                  </button>
                </div>
                <div className="deck-cues-inline">
                  {[6, 7, 8, 9].map((value) => (
                    <button
                      key={`right-inline-${value}`}
                      type="button"
                      onClick={() => handleCuePoint(value)}
                      title={`Jump to cue ${value} on Deck Two`}
                    >
                      {value}
                    </button>
                  ))}
                </div>
                {hasMounted ? (
                  <YouTube
                    videoId={rightDeck.youtubeId}
                    opts={youtubeOpts}
                    onReady={handleRightReady}
                    onStateChange={(event) => handleStateChange("right", event)}
                    className="deck__player"
                  />
                ) : (
                  <div className="deck__player deck__player--placeholder">Loading Deck Two…</div>
                )}
                {renderDeckExtras("right", "Deck Two")}
              </>
            )}
          </div>
        </div>
      </section>
      <section
        className="dj-library-layout"
        style={{ gridTemplateColumns: `${playlistWidth}% minmax(0, ${100 - playlistWidth}%)` }}
      >
        <aside className={`playlist-sidebar panel${collapsedPanels.playlists ? " panel--collapsed" : ""}`}>
          <div className="panel-header panel-header--sidebar">
            <p className="sidebar-title">Playlists</p>
            {renderPanelToggle("playlists", "Playlists")}
          </div>
          {!collapsedPanels.playlists && (
            <div className="playlist-panel-body">
              <ul>
                {playlistFilters.map((playlist) => (
                  <li key={playlist.id} className={selectedPlaylist === playlist.id ? "active" : ""}>
                    <button
                      type="button"
                      onClick={() => setSelectedPlaylist(playlist.id)}
                      title={`Select ${playlist.title}`}
                    >
                      {playlist.title}
                    </button>
                  </li>
                ))}
              </ul>
              <div className="playlist-width-control">
                <label htmlFor="playlist-width">Playlist width</label>
                <input
                  id="playlist-width"
                  type="range"
                  min="20"
                  max="45"
                  value={playlistWidth}
                  onChange={(event) => setPlaylistWidth(Number(event.target.value))}
                  title="Adjust how much space the playlist panel takes"
                />
                <span>{playlistWidth}%</span>
              </div>
            </div>
          )}
        </aside>
        <section className={`dj-playlist panel${collapsedPanels.tracks ? " panel--collapsed" : ""}`}>
          <div className="panel-header panel-header--tracks">
            <div className="panel-header-top">
              <div className="dj-playlist__header">
                <h3>Tracks</h3>
                <div className="dj-playlist__search">
                  <FaSearch />
                  <input
                    type="search"
                    placeholder="Filter titles..."
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    title="Type keywords to narrow the track list"
                  />
                </div>
              </div>
              <div className="panel-header-actions">
                <div className="dj-playlist__meta">
                  <span>{filteredTracks.length} videos</span>
                  <a href={CHANNEL_URL} target="_blank" rel="noreferrer" className="channel-link">
                    Visit #gcc-worship
                    <FaExternalLinkAlt />
                  </a>
                </div>
                {renderPanelToggle("tracks", "Tracks")}
              </div>
            </div>
            <div className="playlist-controls panel-controls">
              <div className="sort-row">
                {SORT_KEYS.map((key) => (
                  <button
                    key={key}
                    type="button"
                    className={`effect-pill${sortBy === key ? " active" : ""}`}
                    onClick={() => handleSortChange(key)}
                    title={`Sort by ${key}`}
                  >
                    {key.toUpperCase()} {sortBy === key ? (sortDirection === "asc" ? "▲" : "▼") : ""}
                  </button>
                ))}
              </div>
              <div className="intelligent-row">
                {INTELLIGENT_FILTERS.map((filter) => (
                  <button
                    key={filter.id}
                    type="button"
                    className={`effect-pill${intelligentFilter === filter.id ? " active" : ""}`}
                    onClick={() => handleIntelligentFilter(filter.id)}
                    title={`Apply intelligent playlist: ${filter.description}`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {!collapsedPanels.tracks && (
            <div className="panel-body">
              {previewTrack && (
                <div className="track-preview">
                  <p>Previewing: {previewTrack.title}</p>
                  <span>{previewTrack.artist}</span>
                  <small>Play this through the headphone monitor (Deck One).</small>
                </div>
              )}
              <div className="dj-playlist__body">
                <div className="track-table">
                  <div className="track-table__header">
                    <span className="track-table__header-cell track-table__cell--index">#</span>
                    <span className="track-table__header-cell track-table__cell--title">Title</span>
                    <span className="track-table__header-cell track-table__cell--artist">Artist</span>
                    <span className="track-table__header-cell track-table__cell--time">Time</span>
                    <span className="track-table__header-cell track-table__cell--bpm">BPM</span>
                    <span className="track-table__header-cell track-table__cell--genre">Genre</span>
                    <span className="track-table__header-cell track-table__cell--link">Link</span>
                    <span className="track-table__header-cell track-table__cell--actions">Actions</span>
                  </div>
                  <div className="track-table__body">
                    {filteredTracks.map((track, index) => (
                      <div className="track-row" key={track.id}>
                        <span className="track-row__cell track-row__cell--index">
                          {index + 1}
                        </span>
                        <div className="track-row__cell track-row__cell--title">
                          <div className="track-thumb">
                            {track.thumbnail ? (
                              <img src={track.thumbnail} alt={track.title} loading="lazy" />
                            ) : (
                              <div className="thumb-placeholder">GCC</div>
                            )}
                          </div>
                          <div className="track-title-block">
                            <span className="track-info__title">{track.title}</span>
                            <small className="track-release">{track.release}</small>
                          </div>
                        </div>
                        <span className="track-row__cell track-row__cell--artist">
                          {track.artist}
                        </span>
                        <span className="track-row__cell track-row__cell--time">
                          {track.duration}
                        </span>
                        <span className="track-row__cell track-row__cell--bpm">
                          <FaStar aria-hidden="true" />
                          <span>{track.bpm} BPM</span>
                        </span>
                        <span className="track-row__cell track-row__cell--genre">
                          {track.genre}
                        </span>
                        <span className="track-row__cell track-row__cell--link">
                          <a
                            href={`https://youtube.com/watch?v=${track.youtubeId}`}
                            target="_blank"
                            rel="noreferrer"
                            aria-label={`Open ${track.title} on YouTube`}
                          >
                            <FaExternalLinkAlt />
                          </a>
                        </span>
                        <div className="track-row__cell track-row__cell--actions">
                          <div className="track-actions">
                            <button
                              type="button"
                              onClick={() => handleTrackLoad(track, "left")}
                              title={`Load ${track.title} into Deck One`}
                            >
                              <span>Load Left</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleTrackLoad(track, "right")}
                              title={`Load ${track.title} into Deck Two`}
                            >
                              <span>Load Right</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => handlePreviewTrack(track)}
                              title="Preview this track in headphones"
                            >
                              <span>Preview</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>
      </section>
    </div>
  );
};

export default DJPage;
